import _get from 'lodash/get';
import _classNames from 'classnames';
import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles, Tooltip, Typography } from '@material-ui/core';
import Mixpanel from 'utils/mixpanelService';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getDurationLabel, getTrackingPageName } from 'utils/stringUtils';
import useWindowSize from 'components/useWindowSize';
import { mapPracticeMethodToIcon } from './constants';
import { PracticeOverlay } from './components/PracticeOverlay';

const GAP = 15;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 271,
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px 0px`,
    border: '1px solid #E9ECED',
    borderRadius: 5,
    cursor: 'pointer',
    boxShadow: '10px 10px 24px 0px rgba(1, 97, 155, 0.05)',
    background: 'linear-gradient(223deg, #E8FAFF, #D3ECFF)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'flex-start',
    width: '100%',
  },
  timeBadge: {
    padding: `0px ${theme.spacing(1)}px`,
    borderRadius: 8,
    backgroundColor: '#f0fcff',
    ...theme.typography.pxSmall,
  },
  methodsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  title: {
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  button: {
    borderRadius: '5px',
  },
  topicsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: GAP,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  topicItem: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f0fcff',
    borderRadius: '5px 5px 0px 0px',
    padding: '4px 10px',
    ...theme.typography.pxSmall,
    '&.hidden': {
      position: 'absolute',
      visibility: 'hidden',
    },
  },
  plus: {
    cursor: 'pointer',
  },
  tooltip: {
    ...theme.typography.pxSmall,
    margin: '10px 0px',
  },
}));

export const PracticeItem = ({ item }) => {
  const classes = useStyles();
  const history = useHistory();
  const { width } = useWindowSize();
  const [showOverlay, setShowOverlay] = useState(false);
  const [invisibleTopics, setInvisibleTopics] = useState([]);
  const plusRef = useRef(null);
  const pillsRef = useRef(null);

  const [resourceItemPageSiteCopy, learningLabSiteCopy] = useSiteCopySelector([
    'resource-item-page',
    'learning-lab',
  ]);

  useEffect(() => {
    if (new URLSearchParams(history.location.search).has('slug', item.slug)) {
      setShowOverlay(true);
    }
  }, [history.location]);

  useLayoutEffect(() => {
    if (!pillsRef.current || !plusRef.current) {
      return;
    }

    const container = pillsRef.current;
    const containerWidth = container.offsetWidth;
    const invisible = [];
    const topicsElements = Array.from(container.children).slice(0, -1);
    const plusWidth = plusRef.current.offsetWidth;
    let itemsWidth = 0;

    topicsElements.forEach((child, index) => {
      itemsWidth +=
        child.offsetWidth + GAP * Number(index !== topicsElements.length - 1);
      if (itemsWidth + plusWidth > containerWidth) {
        invisible.push(topics[index]);
      }
    });

    setInvisibleTopics(invisible);
  }, [width]);

  const topics = _get(item, 'topics', []);

  const onClose = () => {
    history.replace(history.location.pathname + history.location.hash);
    setShowOverlay(false);
  };

  const onStart = () => {
    setShowOverlay(true);
    const pageName = getTrackingPageName();
    Mixpanel.track('Practice Card - Start', {
      page: pageName,
      name: item.name,
      methods: item.methods,
    });
  };

  return (
    <div role="button" tabIndex={0} className={classes.root} onClick={onStart}>
      <div className={classes.header}>
        {item.time && (
          <Typography
            component="span"
            className={classes.timeBadge}
            color="textPrimary"
          >
            {item.time} {getDurationLabel(resourceItemPageSiteCopy, item.time)}
          </Typography>
        )}
        <div className={classes.methodsContainer}>
          {_get(item, 'methods', []).map(method => (
            <Fragment key={method}>
              {mapPracticeMethodToIcon[method]?.()}
            </Fragment>
          ))}
        </div>
      </div>
      <Typography
        variant="h6"
        color="textPrimary"
        align="center"
        className={classes.title}
      >
        {item.name}
      </Typography>
      <Button className={classes.button} variant="contained" color="primary">
        {_get(learningLabSiteCopy, 'pageCopy.startPractice')}
      </Button>
      <div className={classes.topicsContainer} ref={pillsRef}>
        {topics.map(name => (
          <span
            key={name}
            className={_classNames(
              classes.topicItem,
              invisibleTopics.includes(name) && 'hidden',
            )}
          >
            {name}
          </span>
        ))}
        <Tooltip
          classes={{ tooltip: classes.tooltip }}
          placement="top"
          title={invisibleTopics.join(', ')}
          enterTouchDelay={0}
          leaveTouchDelay={2000}
        >
          <span
            ref={plusRef}
            className={_classNames(
              classes.topicItem,
              classes.plus,
              !invisibleTopics.length && 'hidden',
            )}
          >
            ...
          </span>
        </Tooltip>
      </div>
      {showOverlay && (
        <PracticeOverlay
          practice={item}
          onClose={onClose}
          learningLabSiteCopy={learningLabSiteCopy}
        />
      )}
    </div>
  );
};
